<script setup lang="ts">
import { ref, provide, watchEffect, type Ref } from 'vue'
import { Tabs } from '@ark-ui/vue'
import UiText from './UiText.vue'
import UiSelect from './UiSelect.vue'

export interface UiTabItemMeta {
    value: string
    label: string
    count?: number | Ref<number>
    disabled?: boolean
}

export interface UiTabsProps {
    orientation?: 'horizontal' | 'vertical'
    defaultValue?: string
}

const props = defineProps<UiTabsProps>()

const model = defineModel<string>()

const items: Ref<UiTabItemMeta[]> = ref([])

function registerItem(item: UiTabItemMeta) {
    const existingIndex = items.value.findIndex((i) => i.value === item.value)
    if (existingIndex === -1) {
        items.value.push(item)
    } else {
        items.value[existingIndex] = item
    }
}

function unregisterItem(value: string) {
    items.value = items.value.filter((i) => i.value !== value)
}

provide('UiTabs:registerItem', registerItem)
provide('UiTabs:unregisterItem', unregisterItem)

// Set default value when items register
watchEffect(() => {
    if (!model.value && items.value.length > 0) {
        model.value = props.defaultValue || items.value.find(i => !i.disabled)?.value
    }
})

function getCount(item: UiTabItemMeta): number | undefined {
    if (item.count === undefined) return undefined
    if (typeof item.count === 'number') return item.count
    return item.count.value
}
</script>

<template>
    <Tabs.Root v-model="model" :orientation="props.orientation || 'horizontal'" class="w-full min-w-0"
        :class="props.orientation === 'vertical' ? 'grid gap-6 md:grid-cols-[200px_minmax(0,1fr)] md:items-start' : ''">
        <div v-if="props.orientation === 'vertical'" class="md:hidden">
            <UiSelect
                v-model="model"
                aria-label="Select group"
                :options="items.filter(item => !item.disabled).map(item => ({ value: item.value, label: item.label }))"
            />
        </div>
        <!-- Tab List: horizontal scroll when tabs overflow (e.g. narrow mobile) -->
        <div
            :class="props.orientation === 'vertical'
                ? 'hidden md:block md:sticky md:top-6'
                : 'min-w-0 overflow-x-auto overflow-y-hidden border-b border-se-gray-300 touch-pan-x'"
        >
            <Tabs.List :class="props.orientation === 'vertical' ? 'flex flex-col gap-1' : '-mb-px flex min-w-max flex-nowrap space-x-8'">
                <Tabs.Trigger
                    v-for="item in items"
                    :key="item.value"
                    :value="item.value"
                    :disabled="item.disabled"
                    class="font-medium text-sm transition-colors cursor-pointer data-disabled:opacity-50 data-disabled:cursor-not-allowed"
                    :class="props.orientation === 'vertical'
                        ? 'flex w-full items-center justify-between gap-2 rounded-lg px-3 py-3 text-left text-se-gray-600 hover:bg-se-gray-100 data-selected:bg-se-gray-200 data-selected:text-se-dark'
                        : 'shrink-0 whitespace-nowrap py-4 px-1 border-b-2 border-transparent text-se-gray-500 hover:text-se-gray-700 hover:border-se-gray-300 data-selected:border-primary data-selected:text-primary'"
                >
                    <UiText as="span" variant="p-small" :text="item.label" />
                    <span
                        v-if="getCount(item) !== undefined"
                        class="ml-2 py-0.5 px-2 rounded-full text-xs"
                        :class="model === item.value
                            ? 'bg-primary/10 text-primary'
                            : 'bg-se-gray-100 text-se-gray-600'"
                    >
                        {{ getCount(item) }}
                    </span>
                </Tabs.Trigger>
            </Tabs.List>
        </div>

        <!-- Tab Content -->
        <div :class="props.orientation === 'vertical' ? 'min-w-0' : 'mt-6'">
            <slot />
        </div>
    </Tabs.Root>
</template>
